// Copyright (c) Cosmo Tech.
// Licensed under the MIT license.
import React, { useEffect, useState } from 'react';
import PropTypes from 'prop-types';
import CloseIcon from '@mui/icons-material/Close';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  Table,
  TableHead,
  TableRow,
  TableCell,
  TableBody,
  TextField,
  IconButton,
  Box,
  Button,
  Select,
  MenuItem,
  Checkbox,
  FormControlLabel,
  TableSortLabel,
  FormControl,
  FormHelperText,
} from '@mui/material';

const OUTAGE_TYPE_VALUE_TO_LABEL_FR = {
  Lines: 'Lignes',
  ExitLines: 'Lignes de sortie',
  Places: 'Places',
};

const tabConfig = {
  module: {
    label: 'Module',
    dataKey: 'module',
    columns: [
      { key: 'name', label: 'Nom', inputType: 'select' },
      { key: 'alpha', label: 'Alpha', inputType: 'number' },
      { key: 'priorityControlOverStock', label: 'Priorité sécurisation avant stockage', inputType: 'checkbox' },
      {
        key: 'priorityIdentificationOverControl',
        label: 'Priorité identification avant sécurisation',
        inputType: 'checkbox',
      },
    ],
  },
  stockChoiceTable: {
    label: 'Choix des stocks',
    dataKey: 'stockChoiceTable',
    defaultSortKeys: ['currentModule', 'exitModule', 'earlyThreshold'],
    columns: [
      { key: 'currentModule', label: 'Module courant', inputType: 'select' },
      { key: 'exitModule', label: 'Module de sortie', inputType: 'select' },
      {
        key: 'earlyThreshold',
        label: "Délais d'anticipation (minutes)",
        inputType: 'number',
        allowNegative: true,
        storageMultiplier: 60, // Display in minutes, store in seconds
      },
      { key: 'stockName', label: 'Stocker', inputType: 'select' },
    ],
  },
  outages: {
    label: 'Pannes',
    dataKey: 'outages',
    columns: [
      { key: 'entity', label: 'Sous-fonction', inputType: 'text' },
      { key: 'type', label: 'Type', inputType: 'text' },
      { key: 'impact', label: 'Impact', inputType: 'number' },
      { key: 'startTime', label: 'Début', inputType: 'time' },
      { key: 'endTime', label: 'Fin', inputType: 'time' },
    ],
  },
};

function rowHasData(row, columns) {
  return columns.some((col) => {
    const val = row[col.key];
    if (col.inputType === 'checkbox') return !!val;
    return val !== '' && val !== undefined && val !== null;
  });
}

function isEmptyValue(val, inputType) {
  if (inputType === 'checkbox') return false;
  if (inputType === 'number') {
    return val === '' || val === null || val === undefined || Number.isNaN(Number(val));
  }
  return val === '' || val === null || val === undefined;
}

function descendingComparator(a, b, orderBy, inputType) {
  let va = a[orderBy];
  let vb = b[orderBy];

  if (inputType === 'checkbox') {
    va = !!va;
    vb = !!vb;
    return va === vb ? 0 : va ? -1 : 1;
  }

  if (inputType === 'number') {
    va = Number(va);
    vb = Number(vb);
    if (isNaN(va) && isNaN(vb)) return 0;
    if (isNaN(va)) return 1;
    if (isNaN(vb)) return -1;
    return vb - va;
  }

  if (inputType === 'time') {
    if (!va && !vb) return 0;
    if (!va) return 1;
    if (!vb) return -1;
    return vb.localeCompare(va);
  }

  va = (va ?? '').toString().toLowerCase();
  vb = (vb ?? '').toString().toLowerCase();
  return vb.localeCompare(va);
}

function stableSort(array, comparator) {
  const stabilized = array.map((el, idx) => [el, idx]);
  stabilized.sort((a, b) => {
    const cmp = comparator(a[0], b[0]);
    if (cmp !== 0) return cmp;
    return a[1] - b[1];
  });
  return stabilized.map((el) => el[0]);
}

function getSortColsComparator(sortCols, columns) {
  return (a, b) => {
    for (const { key, direction } of sortCols) {
      const col = columns.find((c) => c.key === key);
      if (!col) continue;
      const desc = descendingComparator(a, b, key, col.inputType);
      const cmp = direction === 'desc' ? desc : -desc;
      if (cmp !== 0) return cmp;
    }
    return 0;
  };
}

export default function TabDataDialog({
  open,
  onClose,
  activeTab,
  onSave,
  dynamicOptions = {},
  initialData,
  readOnly = false,
}) {
  const config = activeTab ? tabConfig[activeTab] : null;
  const isReadOnly = readOnly || activeTab === 'outages';
  const [rows, setRows] = useState([]);

  const [sortCols, setSortCols] = useState([]); // [{ key, direction }]

  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (open && config) {
      const existing = initialData?.[config.dataKey];
      if (Array.isArray(existing) && existing.length > 0) {
        const normalized = existing.map((row) =>
          config.columns.reduce(
            (acc, col) => ({
              ...acc,
              [col.key]: row[col.key] != null ? row[col.key] : col.inputType === 'checkbox' ? false : '',
            }),
            {}
          )
        );
        setRows(normalized);
      } else {
        const initial = Array.from({ length: 3 }).map(() =>
          config.columns.reduce(
            (acc, col) => ({
              ...acc,
              [col.key]: col.inputType === 'checkbox' ? false : '',
            }),
            {}
          )
        );
        setRows(initial);
      }
      setSortCols([]);
      setErrors({});
    }
  }, [open, activeTab, config, initialData]);

  if (!open || !config) return null;

  const { label, columns } = config;

  const handleAdd = () =>
    setRows((prev) => [
      ...prev,
      columns.reduce((acc, col) => ({ ...acc, [col.key]: col.inputType === 'checkbox' ? false : '' }), {}),
    ]);

  const handleRemove = (idx) => {
    setRows((prev) => prev.filter((_, i) => i !== idx));
    setErrors((prev) => {
      const next = { ...prev };
      Object.keys(next).forEach((k) => {
        const [r] = k.split('.');
        if (Number(r) === idx) delete next[k];
      });
      const remapped = {};
      Object.keys(next).forEach((k) => {
        const [r, c] = k.split('.');
        const rNum = Number(r);
        remapped[`${rNum > idx ? rNum - 1 : rNum}.${c}`] = true;
      });
      return remapped;
    });
  };

  const handleChange = (idx, key, val) => {
    setRows((prev) => {
      const copy = [...prev];
      copy[idx] = { ...copy[idx], [key]: val };
      return copy;
    });
    setErrors((prev) => {
      const next = { ...prev };
      delete next[`${idx}.${key}`];
      return next;
    });
  };

  const validateActiveRows = () => {
    const newErrors = {};
    rows.forEach((row, rIdx) => {
      const active = rowHasData(row, columns);
      if (!active) return;
      columns.forEach((col) => {
        if (isEmptyValue(row[col.key], col.inputType)) {
          newErrors[`${rIdx}.${col.key}`] = true;
        }
      });
    });
    if (Object.keys(newErrors).length > 0) {
      setErrors(newErrors);
      return false;
    }
    setErrors({});
    return true;
  };

  const handleClose = (event, reason) => {
    if (!isReadOnly) {
      const ok = validateActiveRows();
      if (!ok) {
        window.alert('Veuillez compléter tous les champs requis dans chaque ligne active avant de quitter.');
        return;
      }
      const filteredRows = rows.filter((row) => rowHasData(row, columns));
      onSave(config.dataKey, filteredRows);
    }
    onClose(event, reason);
  };

  const handleRequestSort = (event, property) => {
    setSortCols((prev) => {
      const existing = prev.find((s) => s.key === property);
      if (existing) {
        if (existing.direction === 'asc') {
          // asc → desc
          return prev.map((s) => (s.key === property ? { ...s, direction: 'desc' } : s));
        } else {
          // desc → remove from stack
          return prev.filter((s) => s.key !== property);
        }
      }
      // Not yet in stack → append as next sort level
      return [...prev, { key: property, direction: 'asc' }];
    });
  };

  const cellHasError = (rowIdx, colKey) => !!errors[`${rowIdx}.${colKey}`];

  const renderInput = (rowValue, idx, col) => {
    const options =
      col.key === 'stockName'
        ? dynamicOptions.stockName || []
        : dynamicOptions[col.key] !== undefined
          ? dynamicOptions[col.key]
          : col.options || [];

    const error = cellHasError(idx, col.key);

    if (isReadOnly) {
      let displayValue = rowValue;

      if (activeTab === 'outages' && col.key === 'type') {
        displayValue = OUTAGE_TYPE_VALUE_TO_LABEL_FR[rowValue] ?? rowValue;
      }

      if (col.inputType === 'select') {
        const opt = options.find((o) => (typeof o === 'object' ? o.value === rowValue : o === rowValue));
        if (opt) displayValue = opt.label ?? opt;
      }

      return <TextField variant="standard" fullWidth value={displayValue ?? ''} InputProps={{ readOnly: true }} />;
    }

    const commonProps = {
      required: true,
      error,
      helperText: error ? 'Requis' : undefined,
      variant: 'standard',
      fullWidth: true,
    };

    switch (col.inputType) {
      case 'number': {
        // Apply storageMultiplier for display (e.g., seconds -> minutes)
        const multiplier = col.storageMultiplier || 1;
        const displayValue =
          rowValue === '' || rowValue === null || rowValue === undefined ? '' : Math.round(rowValue / multiplier);
        return (
          <TextField
            {...commonProps}
            type="number"
            value={displayValue}
            onChange={(e) => {
              const v = e.target.value;
              // Convert back to storage units (e.g., minutes -> seconds)
              handleChange(idx, col.key, v === '' ? '' : Number(v) * multiplier);
            }}
            onWheel={(e) => e.target.blur()}
            InputProps={{
              inputProps: col.allowNegative ? {} : { min: 0 },
              sx: {
                '& input[type=number]::-webkit-inner-spin-button, & input[type=number]::-webkit-outer-spin-button': {
                  WebkitAppearance: 'none',
                  margin: 0,
                },
                '& input[type=number]': {
                  MozAppearance: 'textfield',
                },
              },
            }}
          />
        );
      }
      case 'select':
        return (
          <FormControl variant="standard" fullWidth required error={error}>
            <Select
              value={rowValue ?? ''}
              onChange={(e) => handleChange(idx, col.key, e.target.value)}
              displayEmpty
              renderValue={(selected) => {
                if (!selected) return <span style={{ opacity: 0.6 }}>Sélectionnez…</span>;
                const opt = options.find((o) => (typeof o === 'object' ? o.value === selected : o === selected));
                return opt ? (opt.label ?? opt) : selected;
              }}
              MenuProps={{ disablePortal: true }}
            >
              <MenuItem value="" disabled>
                <em>Sélectionnez…</em>
              </MenuItem>
              {options.map((opt) =>
                typeof opt === 'object' ? (
                  <MenuItem key={opt.value} value={opt.value}>
                    {opt.label}
                  </MenuItem>
                ) : (
                  <MenuItem key={opt} value={opt}>
                    {opt}
                  </MenuItem>
                )
              )}
            </Select>
            {error && <FormHelperText>Requis</FormHelperText>}
          </FormControl>
        );

      case 'checkbox':
        return (
          <FormControlLabel
            control={<Checkbox checked={!!rowValue} onChange={(e) => handleChange(idx, col.key, e.target.checked)} />}
            label=""
          />
        );
      case 'time':
        return (
          <TextField
            {...commonProps}
            type="time"
            value={rowValue}
            onChange={(e) => handleChange(idx, col.key, e.target.value)}
          />
        );
      default:
        return (
          <TextField {...commonProps} value={rowValue} onChange={(e) => handleChange(idx, col.key, e.target.value)} />
        );
    }
  };

  const effectiveSortCols =
    sortCols.length > 0
      ? [
          ...sortCols,
          ...(config.defaultSortKeys || [])
            .filter((key) => !sortCols.find((s) => s.key === key))
            .map((key) => ({ key, direction: 'asc' })),
        ]
      : (config.defaultSortKeys || []).map((key) => ({ key, direction: 'asc' }));

  const displayedRows =
    effectiveSortCols.length > 0 ? stableSort(rows, getSortColsComparator(effectiveSortCols, columns)) : rows;

  return (
    <Dialog open={open} onClose={handleClose} maxWidth="lg" fullWidth disablePortal>
      <DialogTitle
        sx={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
        }}
      >
        {label}
        <IconButton onClick={handleClose} size="small">
          <CloseIcon />
        </IconButton>
      </DialogTitle>
      <DialogContent dividers>
        <Table size="small">
          <TableHead>
            <TableRow>
              {columns.map((col) => {
                const colSort = sortCols.find((s) => s.key === col.key);
                const sortIndex = sortCols.findIndex((s) => s.key === col.key);
                return (
                  <TableCell
                    key={col.key}
                    sortDirection={colSort ? colSort.direction : false}
                    sx={{ cursor: isReadOnly ? 'default' : 'pointer', userSelect: 'none' }}
                    onClick={isReadOnly ? undefined : (e) => handleRequestSort(e, col.key)}
                  >
                    <TableSortLabel
                      active={!!colSort}
                      direction={colSort ? colSort.direction : 'asc'}
                      hideSortIcon={isReadOnly}
                      disabled={isReadOnly}
                    >
                      {col.label}
                      {sortCols.length > 1 && sortIndex !== -1 && (
                        <span style={{ fontSize: '0.7em', marginLeft: 2, opacity: 0.7 }}>{sortIndex + 1}</span>
                      )}
                    </TableSortLabel>
                  </TableCell>
                );
              })}
              <TableCell />
            </TableRow>
          </TableHead>
          <TableBody>
            {displayedRows.map((row, idx) => (
              <TableRow key={idx}>
                {columns.map((col) => (
                  <TableCell key={`${col.key}-${idx}`}>{renderInput(row[col.key], rows.indexOf(row), col)}</TableCell>
                ))}
                <TableCell>
                  {!isReadOnly && (
                    <IconButton onClick={() => handleRemove(rows.indexOf(row))} size="small">
                      <CloseIcon fontSize="small" />
                    </IconButton>
                  )}
                </TableCell>
              </TableRow>
            ))}
          </TableBody>
        </Table>
        {!isReadOnly && (
          <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
            <Button variant="outlined" onClick={handleAdd}>
              {activeTab === 'outages' ? 'Ajouter une panne' : 'Ajouter une ligne'}
            </Button>
          </Box>
        )}
      </DialogContent>
    </Dialog>
  );
}

TabDataDialog.propTypes = {
  open: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
  activeTab: PropTypes.oneOf(['module', 'stockChoiceTable', 'outages']),
  onSave: PropTypes.func.isRequired,
  dynamicOptions: PropTypes.object,
  initialData: PropTypes.object,
  readOnly: PropTypes.bool,
};
